import React from 'react';

import { Button } from '@mui/material';
import _ from 'lodash';

import { FieldInputType } from 'constants/fields';
import { FieldType } from 'constants/formGenerator';
import { LICENSE_NUMBER_MAX_LENGTH } from 'constants/loanApplication';

import { ADD_NEW_FEE_PROVIDER } from '../ClosingCompany/ClosingCompany.constants';
import { LoanClosingTestIds } from '../LoanClosing.testIds';

export const closingCompanyEditConfigFields = {
  attendantCompanyId: {
    fieldId: 'attendantCompanyId',
    type: FieldType.SELECT,
    nameInModel: 'attendantCompanyId',
    inputProps: {
      widthInColumns: 3,
      disableClearable: true,
      label: 'Title/Settlement Agent',
      disabled: false,
      required: true,
      variant: 'outlined',
      options: [],
    },
  },
  name: {
    fieldId: 'name',
    type: FieldType.INPUT,
    nameInModel: 'name',
    inputProps: {
      label: 'Provider Name',
      disabled: true,
      required: true,
      variant: 'outlined',
      type: FieldInputType.TEXT,
    },
  },
  streetAddress: {
    fieldId: 'streetAddress',
    type: FieldType.INPUT,
    nameInModel: 'streetAddress',
    inputProps: {
      label: 'Street Address',
      disabled: true,
      required: true,
      variant: 'outlined',
      type: FieldInputType.TEXT,
    },
  },
  suite: {
    fieldId: 'suite',
    type: FieldType.INPUT,
    nameInModel: 'suite',
    inputProps: {
      label: 'Suite',
      disabled: true,
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEXT,
    },
  },
  city: {
    fieldId: 'city',
    type: FieldType.INPUT,
    nameInModel: 'city',
    inputProps: {
      label: 'City',
      disabled: true,
      required: true,
      variant: 'outlined',
      type: FieldInputType.TEXT,
    },
  },
  state: {
    fieldId: 'state',
    type: FieldType.INPUT,
    nameInModel: 'state',
    inputProps: {
      label: 'State',
      disabled: true,
      required: true,
      variant: 'outlined',
      type: FieldInputType.TEXT,
    },
  },
  zipCode: {
    fieldId: 'zipCode',
    type: FieldType.INPUT,
    nameInModel: 'zipCode',
    inputProps: {
      label: 'Zip Code',
      disabled: true,
      required: true,
      variant: 'outlined',
      type: FieldInputType.TEXT,
    },
  },
  phone: {
    fieldId: 'phone',
    type: FieldType.TEL,
    nameInModel: 'phone',
    inputProps: {
      placeholder: 'Type Phone Number...',
      label: 'Phone',
      disabled: true,
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEL,
    },
  },
  email: {
    fieldId: 'email',
    type: FieldType.INPUT,
    nameInModel: 'email',
    inputProps: {
      placeholder: 'Type email...',
      label: 'Email',
      disabled: true,
      required: false,
      variant: 'outlined',
      type: FieldInputType.EMAIL,
    },
  },
  licenseNumber: {
    fieldId: 'licenseNumber',
    type: FieldType.INPUT,
    nameInModel: 'licenseNumber',
    inputProps: {
      label: 'License Number',
      disabled: true,
      required: true,
      variant: 'outlined',
      type: FieldInputType.TEXT,
    },
  },
  escrowNumber: {
    fieldId: 'escrowNumber',
    type: FieldType.INPUT,
    nameInModel: 'escrowNumber',
    inputProps: {
      label: 'Escrow Number',
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEXT_EXCEPT_SPECIAL_SYMBOLS,
    },
  },
  closerContactPhone: {
    fieldId: 'closerContactPhone',
    type: FieldType.TEL,
    nameInModel: 'closerContactPhone',
    inputProps: {
      placeholder: 'Type Phone Number...',
      label: 'Contact Phone',
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEL,
    },
  },
  closerContactEmail: {
    fieldId: 'closerContactEmail',
    type: FieldType.INPUT,
    nameInModel: 'closerContactEmail',
    inputProps: {
      placeholder: 'Type email...',
      label: 'Contact Email',
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.EMAIL,
    },
  },
  closerName: {
    fieldId: 'closerName',
    type: FieldType.INPUT,
    nameInModel: 'closerName',
    inputProps: {
      placeholder: 'Type name...',
      label: 'Closer Name',
      disabled: false,
      required: false,
      variant: 'outlined',
    },
  },
  closerLicenseNumber: {
    fieldId: 'closerLicenseNumber',
    type: FieldType.INPUT,
    nameInModel: 'closerLicenseNumber',
    inputProps: {
      label: 'License Number',
      disabled: false,
      required: false,
      variant: 'outlined',
      type: FieldInputType.TEXT,
      maxLength: LICENSE_NUMBER_MAX_LENGTH,
    },
  },
};

const getAttorneyFields = ({
  filteredCompanyOptions,
  onAttendantCompanyChange,
  sectionLabel,
  sectionPath = '',
  categoryId,
}) => [
  {
    type: FieldType.GROUP,
    title: { label: sectionLabel },
    fields: [
      {
        ...closingCompanyEditConfigFields.attendantCompanyId,
        nameInModel: `${sectionPath}${closingCompanyEditConfigFields.attendantCompanyId.nameInModel}`,
        inputProps: {
          ...closingCompanyEditConfigFields.attendantCompanyId.inputProps,
          options: [
            {
              name: 'Add New Fee Provider',
              value: ADD_NEW_FEE_PROVIDER,
              feeProviderCategoryId: categoryId,
            },
            ...filteredCompanyOptions,
          ],
          label: `Company Name`,
          customOnChange: (e, option) =>
            onAttendantCompanyChange(e, option, sectionPath.slice(0, -1)),
          required: false,
        },
      },
    ],
  },
  {
    type: FieldType.GROUP,
    fields: [
      {
        ...closingCompanyEditConfigFields.name,
        nameInModel: `${sectionPath}${closingCompanyEditConfigFields.name.nameInModel}`,
        inputProps: {
          ...closingCompanyEditConfigFields.name.inputProps,
          label: `Attorney Name`,
        },
      },
      {
        ...closingCompanyEditConfigFields.streetAddress,
        nameInModel: `${sectionPath}${closingCompanyEditConfigFields.streetAddress.nameInModel}`,
      },
      {
        ...closingCompanyEditConfigFields.suite,
        nameInModel: `${sectionPath}${closingCompanyEditConfigFields.suite.nameInModel}`,
        inputProps: {
          ...closingCompanyEditConfigFields.suite.inputProps,
          label: 'Suite',
        },
      },
      {
        ...closingCompanyEditConfigFields.city,
        nameInModel: `${sectionPath}${closingCompanyEditConfigFields.city.nameInModel}`,
      },
      {
        ...closingCompanyEditConfigFields.state,
        nameInModel: `${sectionPath}${closingCompanyEditConfigFields.state.nameInModel}`,
      },
      {
        ...closingCompanyEditConfigFields.zipCode,
        nameInModel: `${sectionPath}${closingCompanyEditConfigFields.zipCode.nameInModel}`,
      },
      {
        ...closingCompanyEditConfigFields.phone,
        nameInModel: `${sectionPath}${closingCompanyEditConfigFields.phone.nameInModel}`,
      },
      {
        ...closingCompanyEditConfigFields.email,
        nameInModel: `${sectionPath}${closingCompanyEditConfigFields.email.nameInModel}`,
      },
      {
        ...closingCompanyEditConfigFields.closerName,
        nameInModel: `${sectionPath}contactPerson`,
        inputProps: {
          ...closingCompanyEditConfigFields.closerName.inputProps,
          label: `Contact Name`,
        },
      },
      {
        ...closingCompanyEditConfigFields.closerContactPhone,
        nameInModel: `${sectionPath}contactPhone`,
      },
      {
        ...closingCompanyEditConfigFields.closerContactEmail,
        nameInModel: `${sectionPath}contactEmail`,
      },
    ],
  },
];

export const getClosingCompanyEditorConfig = ({
  companyOptions,
  closingCompanyType,
  onAttendantCompanyChange,
  onOpenFeeProviderLocationTable,
  isChangeLocationButtonDisabled,
  isAddAttorneys,
  isSellersAttorney,
  isBuyersAttorney,
  categoryId,
}) => {
  const groupedCompanyOptions = _.groupBy(companyOptions, 'type');
  const filteredCompanyOptions = groupedCompanyOptions[closingCompanyType] || [];

  if (isAddAttorneys) {
    return {
      groups: [
        ...getAttorneyFields({
          filteredCompanyOptions,
          onAttendantCompanyChange,
          sectionLabel: `Seller's Attorney`,
          sectionPath: 'seller.',
          categoryId,
        }),
        ...getAttorneyFields({
          filteredCompanyOptions,
          onAttendantCompanyChange,
          sectionLabel: `Buyer's Attorney`,
          sectionPath: 'buyer.',
          categoryId,
        }),
      ],
    };
  }

  if (isSellersAttorney || isBuyersAttorney) {
    return {
      groups: getAttorneyFields({
        filteredCompanyOptions,
        onAttendantCompanyChange,
        sectionLabel: isSellersAttorney ? `Seller's Attorney` : `Buyer's Attorney`,
        categoryId,
      }),
    };
  }

  return {
    groups: [
      {
        type: FieldType.GROUP,
        title: {
          className: 'mt-16',
          label: 'Listing Agent and Seller Information',
        },
        additionalGroupContentConfig: {
          fields: [
            {
              component: () => (
                <div className="w-full">
                  <Button
                    color="primary"
                    data-testid={LoanClosingTestIds.CLOSING_COMPANY_FORM_CHANGE_LOCATION_BUTTON}
                    disabled={isChangeLocationButtonDisabled}
                    type="button"
                    variant="contained"
                    onClick={onOpenFeeProviderLocationTable}
                  >
                    Change location
                  </Button>
                </div>
              ),
            },
          ],
        },
        fields: [],
      },
      {
        type: FieldType.GROUP,
        fieldsClassName: 'flex',
        title: { label: `${closingCompanyType} Company Information` },
        fields: [
          {
            ...closingCompanyEditConfigFields.attendantCompanyId,
            inputProps: {
              ...closingCompanyEditConfigFields.attendantCompanyId.inputProps,
              options: [
                {
                  name: 'Add New Fee Provider',
                  value: ADD_NEW_FEE_PROVIDER,
                  feeProviderCategoryId: categoryId,
                },
                ...filteredCompanyOptions,
              ],
              label: `${closingCompanyType} Company`,
              customOnChange: onAttendantCompanyChange,
            },
          },
        ],
      },
      {
        type: FieldType.GROUP,
        fields: [
          closingCompanyEditConfigFields.name,
          closingCompanyEditConfigFields.streetAddress,
          closingCompanyEditConfigFields.suite,
          closingCompanyEditConfigFields.city,
          closingCompanyEditConfigFields.state,
          closingCompanyEditConfigFields.zipCode,
          closingCompanyEditConfigFields.licenseNumber,
          closingCompanyEditConfigFields.escrowNumber,
        ],
      },
      {
        type: FieldType.GROUP,
        title: { label: 'Closer Information' },
        fields: [
          closingCompanyEditConfigFields.closerContactPhone,
          closingCompanyEditConfigFields.closerContactEmail,
          closingCompanyEditConfigFields.closerName,
          closingCompanyEditConfigFields.closerLicenseNumber,
        ],
      },
    ],
  };
};
